import {
  ExTooltip,
  TooltipPosition,
  TooltipAlignment,
  ExBadge,
  BadgeColor,
  BadgeSize,
  BadgeShape,
  ExIconButton,
  IconButtonSize,
  IconButtonType,
  IconButtonFlavor,
} from '@boomi/exosphere';
import { useTranslation } from 'react-i18next';
import { providerLogo } from '@components/ModelsManagement';
import './ModelCard.scss';

export interface SelectableModel {
  id: string;
  model_id?: string;
  name: string;
  version: string | undefined;
  provider: string;
  boomi: boolean;
  category: 'LLM' | 'SLM';
  runtimeId?: string;
  runtimeName?: string;
  configuration?: string | any;
  description?: string;
  environment?: any;
  configuration_id?: string;
  configuration_count?: number;
  display_name?: string;
}

function renderEnvironmentDetail(environment: any) {
  if (!Array.isArray(environment) || environment.length === 0) {
    return (
      <span className="model-card__detail-value">
        {typeof environment === 'string' ? environment : '-'}
      </span>
    );
  }

  const envNames = environment.map(
    (env: any) => (typeof env === 'string' ? env : env.environment_name || env.name),
  );
  const firstEnv = envNames[0];
  const remaining = envNames.length - 1;
  const displayText = remaining > 0
    ? `${firstEnv} (+${remaining})`
    : firstEnv;

  return (
    <ExTooltip
      position={TooltipPosition.TOP}
      alignment={TooltipAlignment.START}
    >
      <div>{envNames.join(', ')}</div>
      <span slot="anchor" className="model-card__detail-value">
        {displayText}
      </span>
    </ExTooltip>
  );
}

interface ModelCardProps {
  model: any;
  selected: boolean;
  onSelect?: () => void;
  disabled?: boolean;
  showDetails?: boolean;
  onConfigurationClick?: () => void;
}

function ModelCard({
  model,
  selected,
  onSelect,
  disabled,
  showDetails = false,
  onConfigurationClick,
}: ModelCardProps) {
  const [t] = useTranslation('common');
  const isBoomiModel = model.processing_mode === 'standard' || model.processing_mode === 'quick';
  const logo = providerLogo(isBoomiModel ? 'Boomi' : model.provider, isBoomiModel);

  if (showDetails) {
    return (
      <button
        type="button"
        className={`model-card model-card--details${selected ? ' model-card--selected' : ''}${
          disabled ? ' model-card--disabled' : ''
        }`}
        aria-pressed={selected}
        onClick={onConfigurationClick}
        disabled={disabled}
        data-testid={`select-model-card-${model.model_id}`}
      >
        <div className="model-card__details-header">
          <div>
            <span className="model-card__icon">
              <img src={logo.src} alt={logo.alt} />
            </span>
            <h3 className="model-card__details-title">{model.display_name}</h3>
          </div>
          {model.configuration_count !== undefined && (
          <>
            <ExBadge
              color={BadgeColor.WHITE}
              shape={BadgeShape.SQUARED}
              size={BadgeSize.SMALL}
            >
              {model.configuration_count}
              {' '}
              {model.configuration_count === 1 ? t('connection') : t('connections')}
            </ExBadge>
            <ExIconButton
              type={IconButtonType.TERTIARY}
              flavor={IconButtonFlavor.BRANDED}
              icon="Right caret"
              size={IconButtonSize.SMALL}
              onClick={(e) => {
                e.stopPropagation();
                onConfigurationClick?.();
              }}
              data-testid={`select-model-configuration-${model.model_id}`}
            />
          </>
          )}
        </div>

        <div className="model-card__details-content">
          {model.description && (
            <div className="model-card__detail-row">
              <ExTooltip
                position={TooltipPosition.TOP}
                alignment={TooltipAlignment.START}
              >
                <div>
                  {model.description}
                </div>
                <span slot="anchor" className="model-card__detail-value model-card__detail-value--truncate">
                  {model.description}
                </span>
              </ExTooltip>
            </div>
          )}
          {model.runtimeName && (
            <div className="model-card__detail-row">
              <span className="model-card__detail-label">{t('configuration')}</span>
              <span className="model-card__detail-value">{model.configuration}</span>
            </div>
          )}
          {model.environment && (
            <div className="model-card__detail-row">
              <span className="model-card__detail-label">{t('environment')}</span>
              {renderEnvironmentDetail(model.environment)}
            </div>
          )}
        </div>
      </button>
    );
  }
  const modelTypeText = (model.model_id ?? '').toLowerCase().includes('sonnet')
    ? t('model_badge_standard')
    : t('model_badge_fast');
  return (
    <button
      type="button"
      className={`model-card${selected ? ' model-card--selected' : ''}${
        disabled ? ' model-card--disabled' : ''
      }`}
      onClick={disabled ? undefined : onSelect}
      aria-pressed={selected}
      disabled={disabled}
      data-testid={`select-model-card-${model.model_id}`}
    >
      <div className="model-card__header">
        <span className="model-card__icon">
          <img src={logo.src} alt={logo.alt} />
        </span>
        <div className="model-card__header-content">
          <div className="model-card__name-wrapper">
            {isBoomiModel && (
              <>
                <div className="model-card__name">{modelTypeText}</div>
                <span className="model-card__model-type">
                  (
                  {model.display_name}
                  )
                </span>
              </>
            )}
            {!isBoomiModel && (
              <div className="model-card__name">{model.display_name}</div>
            )}
          </div>
        </div>
      </div>
      {model.description && (
        <div className="model-card__description-section">
          <span className="model-card__description-label">{t('description')}</span>
          <ExTooltip
            position={TooltipPosition.TOP}
            alignment={TooltipAlignment.START}
          >
            <div>
              {model.description}
            </div>
            <span slot="anchor" className="model-card__description-text">
              {model.description}
            </span>
          </ExTooltip>
        </div>
      )}
      {model.runtimeName && (
        <div className="model-card__runtime">
          {`Runtime: ${model.runtimeName}`}
        </div>
      )}
    </button>
  );
}

ModelCard.defaultProps = {
  onSelect: undefined,
  disabled: false,
  showDetails: false,
  onConfigurationClick: undefined,
};

export default ModelCard;
